import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import ReactPaginate from 'react-paginate'
import { GET_CONTENTS, getContentsQueryVariables } from 'Queries'
import config from 'Config'
import { PAGE_CONTEXT as p } from 'Constants'
import {
  ErrorView,
  Grid,
  ContentCard,
  CardLoading,
  ContentHeaderLinks,
  ContentFilter,
  HeroArticleCard,
  LoadMoreButton,
  Progress,
  Link,
  HeroArticle,
  Pagination,
  Archive
} from 'Blocks'
import { Loading } from '../components'
import { skin, path } from 'Utils'
import skins from '../skins'
import get from 'lodash/get'

const pageName = p.ARTICLE_LIST

function calculateItemsPerRow(screenType) {
  switch (screenType) {
    case 'small':
      return 1
    case 'medium':
      return 2
    default:
      return 4
  }
}

function LoadingGrid({ screenType, loading }) {
  return loading ? (
    <Grid
      className="article-grid"
      pageName={pageName}
      itemsPerRow={calculateItemsPerRow(screenType)}
    >
      {[1, 2, 3, 4, 5, 6, 7, 8].map((_, index) => (
        <CardLoading key={`${index}-loading`} />
      ))}
    </Grid>
  ) : null
}

LoadingGrid.propTypes = {
  loading: PropTypes.any,
  screenType: PropTypes.any
}

const ArticleListPage = props => {
  const [currentScrollY, setCurrentScrollY] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const { className, location } = props

  const queryString = location.search
  const variables = getContentsQueryVariables(
    queryString,
    config.pagination.articleList,
    p.ARTICLE
  )

  const { data, loading, error, fetchMore, refetch } = useQuery(
    GET_CONTENTS,
    {
      variables,
      notifyOnNetworkStatusChange: true
    },
    { context: pageName }
  )

  useEffect(() => {
    if(config.theme && config.theme === 'v1') {
      const ele = document.querySelector('.global-header');
      if(ele) {
        ele.classList.remove('active');
      }
    }
  }, [])

  useEffect(() => {
    const items = get(data, 'contents.contents', [])
    if (currentScrollY && items.length > config.pagination.articleList) {
      window.scroll(0, currentScrollY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (loading) return <Loading />
  if (error) return null;

  const hasMore = get(data, 'contents.pagination.next', false)
  const items = get(data, 'contents.contents', [])
  const page = parseInt(hasMore - 1)
  const size = parseInt(config.pagination.articleList)
  const currentCount = size * page
  const totalCount = get(data, 'contents.aggregations.count', 0)

  // TODO: move into layout
  let groups = {
    firstGrid: {
      items: items.slice(0, 8),
      itemsPerRow: 4
    },
    secondGrid: {
      items: items.slice(8, 20),
      itemsPerRow: 4
    },
    thirdGrid: {
      items: items.slice(20, 28),
      itemsPerRow: 4
    },
    fourthGrid: {
      items: items.slice(28, 40),
      itemsPerRow: 4
    }
  }
  if (props.screenType === 'medium') {
    groups = {
      firstGrid: {
        items: items.slice(0, 8),
        itemsPerRow: 2
      },
      secondGrid: {
        items: items.slice(8, 20),
        itemsPerRow: 2
      },
      thirdGrid: {
        items: items.slice(20, 28),
        itemsPerRow: 2
      },
      fourthGrid: {
        items: items.slice(28, 40),
        itemsPerRow: 2
      }
    }
  } else if (props.screenType === 'small') {
    groups = {
      firstGrid: {
        items: items.slice(0, 8),
        itemsPerRow: 1
      },
      secondGrid: {
        items: items.slice(8, 20),
        itemsPerRow: 1
      },
      thirdGrid: {
        items: items.slice(20, 28),
        itemsPerRow: 1
      },
      fourthGrid: {
        items: items.slice(28, 40),
        itemsPerRow: 1
      }
    }
  }

  const LoadLink = (type, uuid, slug) => {
    return path(type, { uuid, slug });
  }

  const LoadArticleLayout = () => {
    return (
      <div className={`container article-list-page ${className}`}>
        <ContentHeaderLinks
          title={config.articles.listHeading}
          intro={config.articles.listSubHeading}
          pageName={p.ARTICLE_LIST}
        />
  
        <ContentFilter type={p.ARTICLE} {...{ pageName }} />
  
        {config.heroArticleEnabled && <HeroArticleCard />}
  
        <LoadingGrid loading={loading} screenType={props.screenType} />
  
        <Grid
          className="article-grid"
          pageName={pageName}
          itemsPerRow={groups.firstGrid.itemsPerRow}
        >
          {groups.firstGrid.items.map((content, index) => {
            return (
              <ContentCard
                key={`${index}-${content.uuid}`}
                position={index}
                location={location}
                content={content}
              />
            )
          })}
        </Grid>
  
        <Grid
          className="article-grid"
          pageName={pageName}
          itemsPerRow={groups.secondGrid.itemsPerRow}
        >
          {groups.secondGrid.items.map((content, index) => {
            return (
              <ContentCard
                key={`${index}-${content.uuid}`}
                position={index}
                location={location}
                content={content}
                variant="smallArticle"
              />
            )
          })}
        </Grid>
  
        <Grid
          className="article-grid"
          pageName={pageName}
          itemsPerRow={groups.thirdGrid.itemsPerRow}
        >
          {groups.thirdGrid.items.map((content, index) => {
            return (
              <ContentCard
                key={`${index}-${content.uuid}`}
                position={index}
                location={location}
                content={content}
              />
            )
          })}
        </Grid>
  
        <Grid
          className="article-grid"
          pageName={pageName}
          itemsPerRow={groups.fourthGrid.itemsPerRow}
        >
          {groups.fourthGrid.items.map((content, index) => {
            return (
              <ContentCard
                key={`${index}-${content.uuid}`}
                position={index}
                location={location}
                content={content}
                variant="smallArticle"
              />
            )
          })}
        </Grid>
  
        <Grid
          className="article-grid"
          pageName={pageName}
          itemsPerRow={groups.firstGrid.itemsPerRow}
        >
          {items.slice(40, items.length).map((content, index) => {
            return (
              <ContentCard
                key={`${index}-${content.uuid}`}
                position={index}
                location={location}
                content={content}
                variant={props.screenType === 'small' ? 'smallArticle' : ''}
              />
            )
          })}
        </Grid>
  
        {hasMore && (
          <div className="progress-status">
            <Progress showLabel max={totalCount} value={currentCount} />
            <LoadMoreButton
              onClick={() => {
                setCurrentScrollY(window.scrollY)
                fetchMore({
                  variables: {
                    ...variables,
                    pagination: {
                      page: data.contents.pagination.next,
                      size
                    }
                  }
                })
              }}
              isLoadingMore={loading}
              useLoader
            />
          </div>
        )}
      </div>
    )
  }

  const handlePageClick = async (e) => {
    setCurrentPage(e.selected)
    setCurrentScrollY(window.scrollY)
    await fetchMore({
      variables: {
        ...variables,
        pagination: {
          page: (e.selected + 1),
          size
        }
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if(!fetchMoreResult) return previousResult;
        return fetchMoreResult;
      }
    })
  }

  const sliderCard = {
    hc: 'category-card'
  }

  const contentTag = {
    sb: 'tag-blue',
    hc: 'tag-orange',
    us: 'tag-blue'
  }

  const linkClass = {
    sb: 'slider-card-link',
    hc: 'primary-link link-black',
    us: 'slider-card-link'
  }

  const LoadSbArticleLayout = () => {
    return (
      <div className={props.className}>
        <HeroArticle {...props} />
        <div className="category-section">
          <div className={config.name === 'sb' ? 'section-container-1140' : 'section-container'}>
            <ContentFilter type={p.ARTICLE} {...{ pageName }} />
            <div className="category-content-section">
              {items.map((content, index) => {
                return (
                <div className={`slider-card ${sliderCard[config.name] || ''}`} key={index}>
                  <div className="slider-card-image-block">
                    <div className={`content-tag ${contentTag[config.name]}`}>{content.theme}</div>
                    <Link to={LoadLink(content.type, content.uuid, content.slug)}>
                      <img src={content.image.main} alt="" className="global-image" />
                    </Link>
                  </div>
                  <div className="slider-card-title">{content.title.short}</div>
                  <div className="slider-card-description">{content.title.long}</div>
                  <div className="slider-card-link-holder">
                    <Link to={LoadLink(content.type, content.uuid, content.slug)} className={`${linkClass[config.name] || ''}`}>Read more</Link>
                  </div>
                </div>
                )
              })}
            </div>

            <div className="global-pagination">
              <Pagination
                location={location}
                pagination={data.contents.pagination}
              />
              
              <div className="pagination-status-row">{hasMore ? currentCount : totalCount}/{totalCount} Articles</div>
            </div>
          </div>
        </div>
        {/* <Archive /> */}
      </div>
    )
  }

  return (config && config.theme === 'v1' ? <LoadSbArticleLayout /> : <LoadArticleLayout />)
}

ArticleListPage.propTypes = {
  className: PropTypes.string,
  location: PropTypes.shape({
    search: PropTypes.any
  }),
  screenType: PropTypes.string
}

export default skin(ArticleListPage, skins)
